import { useLazyQuery, useQuery } from '@apollo/client';
import React, { useEffect, useMemo, useState } from 'react';

import { Autocomplete } from '@flixbus/honeycomb-react';

import { FETCH_RIDES_WITH_FILTERS } from 'gql/rideSearch';
import { formatDateDisplay, formatTime } from 'utils/datetime';
import { todayIsoWindow } from 'utils/datetime/fnsTimeFormatter';

import { CREATE_BREAKDOWN_FLOW_TEXT } from '../texts';
import { rideOptionTitle } from './parallelRideOption';

const TEXT = CREATE_BREAKDOWN_FLOW_TEXT.parallelRide;

const PAGE_SIZE = 50;

const rideOptionSubtitle = (ride) => {
	if (!ride?.departure) return '';
	return `Departure: ${formatDateDisplay(ride.departure)} at ${formatTime(ride.departure)}`;
};

const rideToOption = (ride) => ({
	title: rideOptionTitle(ride),
	subtitle: rideOptionSubtitle(ride),
	value: ride.id,
	data: ride,
});

const todayWindow = () => {
	const { from, to } = todayIsoWindow();
	return { departureFromDateTime: from, departureToDateTime: to };
};

const parseTripNumber = (value) => {
	const trimmed = value.trim();
	if (!/^\d+$/.test(trimmed)) return null;
	return Number(trimmed);
};

/**
 * Parallel-ride picker (autocomplete).
 *
 * On mount, seeds options with today's rides (start-of-today →
 * end-of-today, user local TZ). Once the user types digits, switches to a
 * trip-number filter. Non-integer input clears options.
 *
 * `onChange` is called with two arguments: the selected `rideId` (or `null`)
 * and the full ride object (or `null`). The parent leaf uses the ride to
 * derive the `outboundTraveling` window from the first/last station times.
 *
 * `incidentTimestamp` is accepted for API stability with earlier callers
 * but no longer influences the initial-load window (it is intentionally
 * ignored).
 *
 * @param {{
 *   value: string|null,
 *   onChange: (rideId: string|null, ride: Object|null) => void,
 *   incidentTimestamp?: string|null,
 * }} props
 */
// eslint-disable-next-line no-unused-vars
const ParallelRideInputs = ({ value, onChange, incidentTimestamp = null }) => {
	const initialFilter = useMemo(() => todayWindow(), []);

	const {
		data: initialData,
		loading: initialLoading,
		error: initialError,
	} = useQuery(FETCH_RIDES_WITH_FILTERS, {
		variables: {
			pagination: { page: 0, size: PAGE_SIZE },
			filter: initialFilter,
		},
		fetchPolicy: 'no-cache',
	});

	const [getRidesByTripNumber, { loading: searchLoading }] = useLazyQuery(
		FETCH_RIDES_WITH_FILTERS,
		{
			fetchPolicy: 'no-cache',
			notifyOnNetworkStatusChange: true,
		}
	);

	const [inputValue, setInputValue] = useState('');
	const [searchOptions, setSearchOptions] = useState(null);

	const initialOptions = useMemo(
		() => (initialData?.rideSearch?.results ?? []).map(rideToOption),
		[initialData]
	);

	const optionsToShow = searchOptions ?? initialOptions;
	const loading = searchOptions === null ? initialLoading : searchLoading;

	const selectedValue = useMemo(() => {
		if (!value) return [];
		const source = searchOptions ?? initialOptions;
		const match = source.find((opt) => opt.value === value);
		return match ? [{ title: match.title, value: match.value }] : [];
	}, [value, searchOptions, initialOptions]);

	// Keep the input text in sync with the selected value on mount / when
	// options load in.
	useEffect(() => {
		if (selectedValue.length > 0 && !inputValue) {
			setInputValue(selectedValue[0].title);
		}
	}, [selectedValue, inputValue]);

	const handleInputChange = (event) => {
		const nextInputValue = event.target.value;
		setInputValue(nextInputValue);
		if (nextInputValue === '') {
			setSearchOptions(null);
			onChange(null, null);
		}
	};

	const handleDebounce = async (typedValue) => {
		if (!typedValue) {
			setSearchOptions(null);
			return initialOptions;
		}
		const tripNumber = parseTripNumber(typedValue);
		if (tripNumber === null) {
			setSearchOptions([]);
			return [];
		}
		try {
			const { data } = await getRidesByTripNumber({
				variables: {
					pagination: { page: 0, size: PAGE_SIZE },
					filter: { tripNumbers: [tripNumber] },
				},
			});
			const mapped = (data?.rideSearch?.results ?? []).map(rideToOption);
			setSearchOptions(mapped);
			return mapped;
		} catch {
			setSearchOptions([]);
			return [];
		}
	};

	const handleChange = (selectedOptions) => {
		const selectedList = Array.isArray(selectedOptions) ? selectedOptions : [];
		if (selectedList.length === 0) {
			onChange(null, null);
			setInputValue('');
			return;
		}
		const [selected] = selectedList;
		const source = searchOptions ?? initialOptions;
		const ride = source.find((opt) => opt.value === selected.value)?.data ?? null;
		onChange(selected.value, ride);
		setInputValue(selected.title);
	};

	return (
		<Autocomplete
			{...(selectedValue.length ? { value: selectedValue } : {})}
			inputValue={inputValue}
			options={optionsToShow}
			debounce={300}
			onDebounce={handleDebounce}
			onChange={handleChange}
			comboboxProps={{
				loading,
				label: TEXT.label,
				placeholder: TEXT.placeholder,
				onChange: handleInputChange,
				valid: !!value || undefined,
				infoError: initialError ? TEXT.error(initialError.message) : undefined,
			}}
			listBoxProps={{
				label: TEXT.label,
				optionsToDisplay: 5,
			}}
			data-testid="parallel-ride-autocomplete"
		/>
	);
};

export default ParallelRideInputs;
